/**
 * Age category mapping configuration and utilities
 * 
 * Maps local age category types (adult, child, infant) to Mews AgeCategoryId values.
 * This mapping is configurable and can be updated when age categories change.
 * 
 * @module age-category-mapping
 */

/**
 * Age category mapping configuration
 * 
 * Maps local age category types to Mews AgeCategoryId.
 * 
 * Format: { [localAgeType]: mewsAgeCategoryId }
 * 
 * Example:
 * {
 *   "adult": "f0b9a9a6-1388-456a-b38b-b02100c54d60",
 *   "child": "7a1a4c03-88fd-4d83-89a4-b02100c54e3a",
 *   "infant": "23e61965-e8fe-491b-a86d-b02600aa149b"
 * }
 */
export interface AgeCategoryMappingConfig {
  adult: string; // Mews AgeCategoryId for adults
  child: string; // Mews AgeCategoryId for children
  infant?: string; // Optional: Mews AgeCategoryId for infants
}

/**
 * Default age category mapping configuration
 * 
 * This is populated from:
 * 1. Environment variables (VITE_MEWS_AGE_CATEGORY_MAPPING_JSON)
 * 2. Auto-matching fallback (name/age-based detection)
 */
let ageCategoryMappingCache: Partial<AgeCategoryMappingConfig> = {};

// Automatically load mapping from environment variable on module initialization
// This allows manual mappings to be configured via .env.local
if (typeof window !== 'undefined') {
  // Only run in browser environment
  loadAgeCategoryMappingFromEnv();
}

/**
 * Gets the current age category mapping configuration
 * 
 * @returns Current age category mapping configuration
 */
export function getAgeCategoryMapping(): Partial<AgeCategoryMappingConfig> {
  return { ...ageCategoryMappingCache };
}

/**
 * Sets the age category mapping configuration
 * 
 * @param mapping - Age category mapping configuration to set
 */
export function setAgeCategoryMapping(mapping: Partial<AgeCategoryMappingConfig>): void {
  ageCategoryMappingCache = { ...mapping };
}

/**
 * Clears the age category mapping cache
 */
export function clearAgeCategoryMapping(): void {
  ageCategoryMappingCache = {};
}

/**
 * Gets Mews AgeCategoryId for a local age category type
 * 
 * @param localAgeType - Local age category type ("adult", "child", or "infant")
 * @returns Mews AgeCategoryId or undefined if not found
 */
export function getMewsAgeCategoryId(localAgeType: 'adult' | 'child' | 'infant'): string | undefined {
  return ageCategoryMappingCache[localAgeType];
}

/**
 * Loads age category mapping from environment variable
 * 
 * Expects VITE_MEWS_AGE_CATEGORY_MAPPING_JSON to contain a JSON string
 * with the age category mapping configuration.
 * 
 * @returns True if mapping was loaded successfully, false otherwise
 */
export function loadAgeCategoryMappingFromEnv(): boolean {
  const envMapping = import.meta.env.VITE_MEWS_AGE_CATEGORY_MAPPING_JSON;
  if (!envMapping) {
    return false;
  }

  try {
    const mapping = JSON.parse(envMapping) as AgeCategoryMappingConfig;
    setAgeCategoryMapping(mapping);
    
    if (import.meta.env.DEV) {
      console.log('[Age Category Mapping] Loaded from environment:', {
        adult: mapping.adult ? `${mapping.adult.substring(0, 8)}...` : 'not set',
        child: mapping.child ? `${mapping.child.substring(0, 8)}...` : 'not set',
        infant: mapping.infant ? `${mapping.infant.substring(0, 8)}...` : 'not set',
      });
    }
    
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Age Category Mapping] Failed to parse age category mapping from environment variable:', error);
    }
    return false;
  }
}
